import { useEffect, useState } from "react";
import { CheckCircle, MessageSquareText, Send } from "lucide-react";
import { request } from "./FunctionalApp";

async function api(path: string, init: RequestInit = {}) {
  const r = await request(path, init);
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(String(body.error || "Error").replaceAll("_", " "));
  return body;
}

const CATEGORY_LABELS: Record<string, string> = {
  PROBLEM: "Something didn't work",
  CONFUSING: "This was confusing",
  SUGGESTION: "Feature suggestion",
  OTHER: "Other",
};

export function FeedbackWorkspace() {
  const [notes, setNotes] = useState<any[]>([]);
  const [finalNote, setFinalNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function load() {
    try {
      const d = await api("/api/feedback");
      setNotes(d.notes || []);
    } catch (e: any) { setMessage(e.message); }
  }

  useEffect(() => { load(); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!notes.length && !finalNote.trim()) {
      setMessage("No feedback notes to submit yet — use the Feedback button while testing to log notes as you go.");
      return;
    }
    setBusy(true); setMessage("");
    try {
      await api("/api/feedback/submit", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ finalNote: finalNote.trim() }),
      });
      setSubmitted(true);
      setNotes([]);
      setFinalNote("");
    } catch (e: any) { setMessage(e.message); }
    finally { setBusy(false); }
  }

  if (submitted) return (
    <div className="feedback-submitted">
      <CheckCircle size={48} />
      <h2>Feedback submitted — thank you!</h2>
      <p>Your notes have been sent. Feel free to keep testing and log more feedback anytime using the button in the bottom-right corner.</p>
    </div>
  );

  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Test feedback</h1>
          <p>Review your session notes and submit them when you're done testing.</p>
        </div>
      </div>
      {message && <div className="func-admin-note">{message}</div>}

      <section className="func-panel feedback-notes">
        <div className="func-table-head">
          <h2><MessageSquareText size={15} /> Your session notes</h2>
          <small style={{marginLeft:"auto",color:"var(--muted)",fontSize:11}}>
            Logged using the Feedback button while testing
          </small>
        </div>
        {notes.length === 0 ? (
          <div className="feedback-empty">
            <p>No notes logged yet.</p>
            <p>Use the <b>Feedback</b> button in the bottom-right corner as you test — it captures which page you're on automatically.</p>
          </div>
        ) : (
          <div className="feedback-note-list">
            {notes.map((n: any) => (
              <div key={n.id} className="feedback-note-item">
                <div className="feedback-note-meta">
                  <span className={`feedback-badge feedback-badge-${n.category.toLowerCase()}`}>
                    {CATEGORY_LABELS[n.category] || n.category}
                  </span>
                  <em>📍 {n.page || "—"}</em>
                  <small>{new Date(n.created_at).toLocaleString("en-GB", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}</small>
                </div>
                <p>{n.message}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="func-panel simple-form">
        <div className="func-table-head"><h2><Send size={15} /> Submit to TaskRoute</h2></div>
        <form onSubmit={submit} style={{padding:"0 20px 20px"}}>
          <label>
            Final notes (optional)
            <textarea
              rows={4}
              placeholder="Any overall thoughts, things you liked, or anything else before you submit…"
              value={finalNote}
              onChange={e => setFinalNote(e.target.value)}
            />
          </label>
          <p style={{fontSize:12,color:"var(--muted)",margin:"0 0 12px"}}>
            This will email all your logged notes to TaskRoute staff in one go. You can keep testing afterwards — your next notes will start a fresh batch.
          </p>
          <button disabled={busy || (notes.length === 0 && !finalNote.trim())}>
            <Send size={14} /> {busy ? "Sending…" : "Submit feedback"}
          </button>
        </form>
      </section>
    </>
  );
}
